import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, ViewChild, SimpleChanges } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, } from "@angular/forms";
import { Customer, CustomerContactRequestVO, CustomerUpdateDTO, ShopProductFamily, } from "@dto";
import { TranslateService } from "@ngx-translate/core";
import { CartService } from "@service/cart/cart.service";
import { CustomerClientService } from "@service/client/customer-client.service";
import { PopupService } from "@service/popup/popup.service";
import { WindowRefService } from "@service/window-service/window-ref.service";
import { allRequiredPoliciesAreAccepted, getCollapsingContent, getSimpleContent, isCollapsingContentShown, isSimpleContentShown, parseContent, setAgreementTextExpand, toggleCheckbox, } from "app/utils/utilsFunctionsContent";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { animateButton, getImageUrl, removeAnimateButton, showAfterSecond, } from "../../../../utils/utilsFunctions";
import { BlockService } from "../../block/shared/block.service";
import { CustomValidators } from "../../../../utils/custom.validators";
import { Consent, ConsentType, MessageItem } from "app/model/interfaces";

enum ControlNames
{
  EMAIL = "productStatusEmail",
  PHONE = "productStatusPhone",
}

@Component({
  selector: "app-product-status-popup",
  templateUrl: "./product-status-popup.component.html",
  styleUrls: ["../../shared/modals/contact-popup-abstract/contact-popup-abstract.component.scss", "./product-status-popup.component.scss",],
  standalone: false
})
export class ProductStatusPopupComponent implements OnInit, OnChanges, OnDestroy
{
  public isCollapsingContentShown = isCollapsingContentShown;
  public isSimpleContentShown = isSimpleContentShown;
  public getSimpleContent = getSimpleContent;
  public getCollapsingContent = getCollapsingContent;
  public setAgreementTextExpand = setAgreementTextExpand;

  @ViewChild("addButton") public addButton: ElementRef;

  public isSubmitted: boolean = false;
  public readonly ControlNames = ControlNames;
  public allCheckboxIsSetAsActive: boolean = false;
  public customer: Customer;
  public showForm = false;
  public form: UntypedFormGroup;
  public formMessage = null;
  public error: MessageItem;
  public isLoading = false;
  public submission = false;
  public hideCheckbox = false;
  public buttonText: string;
  public isForNewsletter: boolean = false;
  public formMessageColor: string = "green";
  public defaultPictureSrcSet: string;
  private destroy$: Subject<void> = new Subject();
  public ratingError: boolean = false;
  public agreements: Consent[];
  public loaded = [];

  @Input() public buttonType: string = "secondary-button";
  @Input() public defaultButtonText: string;
  @Input() public product: ShopProductFamily;
  @Input() public navigationBarTitle: string;
  @Input() public mainTitle: string;
  @Input() public name: string = "";
  @Input() public pictureAltName: string;
  @Input() public dropdownOptions: any[] = [];
  @Input() public secondaryTitle: string;
  @Input() public phoneRequired: boolean = false;

  @Input() public webpPictureSrcSet: string;
  @Input() public jpgPictureSrcSet: string;
  @Input() public tooltip: boolean = false;
  @Input() public productCode: string;
  @Input() public phonePlaceholder: string;
  @Input() public emailPlaceholder: string;
  @Input() public withImage: boolean = false;

  @Output() public readonly successEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() public readonly closeParentEventEmitter: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    public renderer: Renderer2,
    public cartService: CartService,
    public windowRef: WindowRefService,
    public elementRef: ElementRef,
    public changeDetector: ChangeDetectorRef,
    private blockService: BlockService,
    private translate: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private customerClientService: CustomerClientService,
    private popupService: PopupService
  )
  {
  }

  public ngOnInit(): void
  {
    this.popupService.reserveName(this.name);

    this.form = this.formBuilder.group({});
    this.addControl(ControlNames.PHONE, this.phoneRequired ? [Validators.required] : null);
    this.addControl(ControlNames.EMAIL, [Validators.required, CustomValidators.email]);
    this.form.addControl(ConsentType.ContactRequestProcessing, new UntypedFormControl(false));
    this.form.addControl(ConsentType.PrivacyPolicy, new UntypedFormControl(false));
    this.form = new UntypedFormGroup(this.form.controls);
    this.form.valueChanges.subscribe(() =>
    {
      this.checkAndRemoveButtonErrorState();
    });

    this.buttonText = this.defaultButtonText;
    this.customerClientService.customer$.pipe(filter((v) => !!v), takeUntil(this.destroy$)).subscribe((newCustomer: Customer) =>
    {
      this.customer = newCustomer;

      //#region Fill up from contact
      if (this.customer && this.form.controls)
      {
        if (this.customer.defaultDeliveryAddress)
        {
          if (this.form.get(ControlNames.PHONE))
          {
            this.form.controls[ControlNames.PHONE].setValue(this.customer.defaultDeliveryAddress ? this.customer.defaultDeliveryAddress.phone : null);
          }
        }

        if (this.form.get(ControlNames.EMAIL))
        {
          this.form.controls[ControlNames.EMAIL].setValue(this.customer.email);
        }
      }
      //#endregion

      //#region Data agreement
      if (this.agreements)
      {
        return;
      }

      const agreements = this.blockService.getDataSync("consents").items.filter(o => o.code == "Contact")[0].consents;
      this.agreements = [];
      agreements.forEach((agreement: Consent) => { this.agreements.push({ ...agreement }); });
      this.agreements.forEach((agr) => (agr.expand = false));
      this.agreements = this.agreements.filter((agr) => !(agr.global == true && agr.accepted));
      this.agreements.filter((agr) => (agr.accepted = false));
      this.agreements.forEach((agreement) => (parseContent(agreement, agreement.text)));
      this.allCheckboxIsSetAsActive = this.agreements.filter((agr) => agr.accepted == true).length == this.agreements.length;
      this.agreements.filter((agr) => agr.defaultChecked == true).forEach((agr) => (agr.accepted = true));

      if (!this.changeDetector["destroyed"])
      {
        this.changeDetector.detectChanges();
      }
      //#endregion
    });

    this.defaultButtonText = this.translate.instant("sendMessage");
    this.emailPlaceholder = this.translate.instant("login.inputEmail");
    this.phonePlaceholder = this.translate.instant("typePhoneNumber");
  }

  private addControl(controlName, controlValidator = [Validators.required]): void
  {
    this.form.addControl(controlName, new UntypedFormControl("", controlValidator));
  }

  public openModal(productVariant?: any, imageThumb?: string, disableReload?: boolean): void
  {
    this.windowRef.blockScroll();
    if (imageThumb)
    {
      if (imageThumb.includes("https"))
      {
        this.webpPictureSrcSet = imageThumb;
        this.jpgPictureSrcSet = imageThumb;
      }
      else
      {
        //only for hover avaiable popup
        this.webpPictureSrcSet = getImageUrl(imageThumb, "0", null, [40, 40, 40], null, true);
        this.jpgPictureSrcSet = getImageUrl(imageThumb, "0", null, [40, 40, 40], null, false);
        this.defaultPictureSrcSet = getImageUrl(imageThumb, "0", null, [40, 40, 40], "100", false);
      }
    }
    this.showForm = true;

    if (this.tooltip)
    {
      this.product = productVariant;
      if (this.windowRef.getDocumentBody().querySelector(".contact-popup-abstract"))
      {
        this.renderer.addClass(this.windowRef.getDocumentBody().querySelector(".contact-popup-abstract"), "first-stage");
      }
    }

    showAfterSecond(100, this.loaded, this.changeDetector);

    this.changeDetector.detectChanges();
    this.popupService.openPopup(this.name, !!disableReload);
  }

  public ngOnDestroy(): void
  {
    this.popupService.clearName(this.name);
    this.destroy$.next();
    this.destroy$.complete();
  }

  public closeModal(event?: any, closeParent?: boolean, disableReload?: boolean): void
  {
    if (event)
    {
      event.stopPropagation();
    }

    this.showForm = false;
    this.popupService.closePopup(this.name, !!disableReload);
    this.error = null;
    this.formMessage = null;
    this.changeDetector.detectChanges();
    this.submission = false;
    this.isLoading = false;
    this.form.reset();
    Object.keys(this.form.controls).forEach((key) =>
    {
      this.form.controls[key].setErrors(null);
    });
    this.loaded["loaded"] = false;

    if (closeParent)
    {
      this.closeParentEventEmitter.emit();
      // We need to unblock scroll after popup close
      setTimeout(() =>
      {
        this.windowRef.unBlockScroll();
      }, 100);
    } else if (!this.popupService.isActivePopup())
    {
      // We need to unblock scroll after popup close
      setTimeout(() =>
      {
        this.windowRef.unBlockScroll();
      }, 100);
    }
  }


  public toggleCheckbox(agreements, code, $event, changeDetector)
  {
    toggleCheckbox(agreements, code, $event, changeDetector);
    this.checkAndRemoveButtonErrorState();
  }

  private checkAndRemoveButtonErrorState()
  {
    if (this.addButton && this.agreements && allRequiredPoliciesAreAccepted(this.agreements) && this.form.valid)
    {
      removeAnimateButton("error", this.addButton, this.renderer);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes.defaultButtonText)
    {
      this.buttonText = this.defaultButtonText;
    }
  }

  public onSubmit(): void
  {
    this.isLoading = true;
    this.isSubmitted = true;
    animateButton("loading", this.addButton, this.renderer, this.changeDetector);
    this.formMessageColor = "green";
    this.submission = true;

    if (!allRequiredPoliciesAreAccepted(this.agreements) || this.form.invalid)
    {
      removeAnimateButton("loading", this.addButton, this.renderer);
      animateButton("error", this.addButton, this.renderer, this.changeDetector);
      this.isLoading = false;

      //#region Mark as touced and dirty 
      if (!this.form.valid)
      {
        Object.keys(this.form.controls).forEach((el) =>
        {
          if (this.form.controls[el].value || this.form.controls[el].errors)
          {
            this.form.controls[el].markAsDirty();
            this.form.controls[el].markAsTouched();
          }
        });
      }
      //#endregion
      this.changeDetector.detectChanges();

      return;
    }

    this.formMessage = null;
    //#region Save for contact request
    const requestDTO: CustomerContactRequestVO = {
      contactFormCode: this.mainTitle,
      customerEmail: !this.form.controls[ControlNames.EMAIL] ? null : this.form.controls[ControlNames.EMAIL].value,
      customerPhone: !this.form.controls[ControlNames.PHONE] ? null : this.form.controls[ControlNames.PHONE].value,
      productName: this.product["name"]
    };

    if (!this.productCode)
    {
      requestDTO.productCode = this.product["code"];
    }

    this.customerClientService.sendContactRequest(requestDTO).subscribe(
      () =>
      {
        this.successEmitter.emit({ type: "Success", message: this.translate.instant("error.formSend"), });

        this.isLoading = false;
        this.error = null;
        this.hideCheckbox = true;
        this.formMessage = this.translate.instant("error.thanksForMessage");

        removeAnimateButton("error", this.addButton, this.renderer);
        removeAnimateButton("loading", this.addButton, this.renderer);
        animateButton("success", this.addButton, this.renderer, this.changeDetector);
        this.changeDetector.detectChanges();
        this.closeModal(null, true);

        if (this.customerClientService.isUserLogged)
        {
          const dto: CustomerUpdateDTO = { addConsents: [] }

          this.agreements.forEach((agr) =>
          {
            if (agr.accepted)
            {
              dto.addConsents.push(agr.code);
            }
          });
        }
      },
      (error) =>
      {
        this.isLoading = false;
        this.error = error;
        this.submission = false;
        this.formMessageColor = "red";
        this.formMessage = this.translate.instant("error.messageSendUnsuccessful");
        removeAnimateButton("success", this.addButton, this.renderer);
        removeAnimateButton("loading", this.addButton, this.renderer);
        animateButton("error", this.addButton, this.renderer, this.changeDetector);
      }
    );
    //#endregion
    this.changeDetector.detectChanges();
  }

  protected onSuccess(success: boolean): void
  {
    this.error = null;
    this.isLoading = false;
    this.isSubmitted = false;
    removeAnimateButton("error", this.addButton, this.renderer);
    removeAnimateButton("loading", this.addButton, this.renderer);
    animateButton("success", this.addButton, this.renderer, this.changeDetector);

    if (success)
    {
      this.form.reset();
      this.changeDetector.detectChanges();
      this.closeModal(null, true);
      return;
    }
  }

  protected onError(error: any): void
  {
    this.isLoading = false;
    this.error = error;
    removeAnimateButton("success", this.addButton, this.renderer);
    removeAnimateButton("loading", this.addButton, this.renderer);
    animateButton("error", this.addButton, this.renderer, this.changeDetector);
    return;
  }

  @HostListener("window:keyup", ["$event"])
  private onKeyUp(event): void
  {
    if (this.showForm && event.key === "Escape")
    {
      this.closeModal(null);
    }
    else if (this.showForm && event.keyCode === 13)
    {
      this.onSubmit();
    }
  }

  @HostListener("window:popstate", ["$event"])
  private backButtonHandler(): void
  {
    if (!this.popupService.isActiveParam("m", this.name) && this.popupService.isActivePopup(this.name))
    {
      this.closeModal(null, null, true);
    }
  }
}
